import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  TableCell as MuiTableCell
} from '@mui/material';
import { TrendingUp, TrendingDown, Remove } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import Title from '../../components/Title';
import { useGetParkingDailyStatsQuery } from '../../redux/api/statisticsApi';
import {
  getManagementCompaniesAsync,
  selectManagementCompanies
} from '../../redux/slices/managementCompaniesSlice';
import {
  ParkingDailyStatsParams,
  ParkingDailyStatRow
} from '../../resources/types/statisticsTypes';

const MAX_PERIOD_DAYS = 100;

const Root = styled('div')({
  padding: 0,
  minHeight: 'calc(100vh - 180px)'
});

const FilterSection = styled(Box)({
  marginBottom: 24,
  display: 'flex',
  gap: 16,
  flexWrap: 'wrap',
  alignItems: 'center'
});

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'light'
        ? 'rgba(0, 0, 0, 0.04)'
        : 'rgba(255, 255, 255, 0.08)'
  },
  transition: 'background-color 0.2s ease'
}));

const StyledTableCell = styled(MuiTableCell)(({ theme }) => ({
  borderBottom: `1px solid ${
    theme.palette.mode === 'light'
      ? 'rgba(0, 0, 0, 0.08)'
      : 'rgba(255, 255, 255, 0.1)'
  }`,
  padding: '12px 16px'
}));

const TableHeaderCell = styled(MuiTableCell)(({ theme }) => ({
  fontWeight: 700,
  backgroundColor: theme.palette.mode === 'light' ? '#F5F5F5' : '#2A2A2A',
  borderBottom: `2px solid ${
    theme.palette.mode === 'light'
      ? 'rgba(0, 0, 0, 0.12)'
      : 'rgba(255, 255, 255, 0.12)'
  }`,
  padding: '12px 16px',
  whiteSpace: 'nowrap'
}));

const StatCard = styled(Paper)({
  padding: '20px 24px',
  borderRadius: '12px',
  display: 'flex',
  flexDirection: 'column',
  gap: 8
});

interface AggregatedHotel {
  parking: string;
  parkingName: string;
  parkingAddress: string;
  managementCompanyName: string | null;
  days: number;
  totalRides: number;
  totalRevenue: number;
  totalCaptured: number;
  scooterDays: number;
  totalIn: number;
  totalOut: number;
  avgScooters: number;
  ridesPerScooter: number;
  revenuePerScooter: number;
}

type Recommendation = 'add' | 'relocate' | 'balanced';

type SortKey =
  | 'parkingName'
  | 'avgScooters'
  | 'totalRides'
  | 'ridesPerScooter'
  | 'totalRevenue'
  | 'revenuePerScooter';

const ADD_THRESHOLD = 1.2;
const RELOCATE_THRESHOLD = 0.6;

const RentalStatisticsPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const managementCompaniesState = useSelector(selectManagementCompanies);

  const today = moment().format('YYYY-MM-DD');
  const defaultFrom = moment().subtract(29, 'days').format('YYYY-MM-DD');

  const [dateFrom, setDateFrom] = useState(defaultFrom);
  const [dateTo, setDateTo] = useState(today);
  const [company, setCompany] = useState('');
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('ridesPerScooter');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const [appliedParams, setAppliedParams] = useState<ParkingDailyStatsParams>({
    dateFrom: defaultFrom,
    dateTo: today
  });

  const { data, isFetching, isError } = useGetParkingDailyStatsQuery(appliedParams);

  useEffect(() => {
    dispatch(getManagementCompaniesAsync(true));
  }, [dispatch]);

  const handleApply = () => {
    if (!dateFrom) {
      setError(t('rentalStats.errDateFrom'));
      return;
    }
    const to = dateTo || today;
    if (moment(to).isBefore(moment(dateFrom))) {
      setError(t('rentalStats.errDateOrder'));
      return;
    }
    if (moment(to).diff(moment(dateFrom), 'days') + 1 > MAX_PERIOD_DAYS) {
      setError(t('rentalStats.errMaxPeriod'));
      return;
    }
    setError('');
    setAppliedParams({
      dateFrom,
      dateTo: to,
      managementCompany: company || undefined
    });
  };

  const aggregated = useMemo<AggregatedHotel[]>(() => {
    const rows: ParkingDailyStatRow[] = data?.data ?? [];
    const map = new Map<string, AggregatedHotel>();

    rows.forEach(row => {
      let item = map.get(row.parking);
      if (!item) {
        item = {
          parking: row.parking,
          parkingName: row.parkingName,
          parkingAddress: row.parkingAddress,
          managementCompanyName: row.managementCompanyName,
          days: 0,
          totalRides: 0,
          totalRevenue: 0,
          totalCaptured: 0,
          scooterDays: 0,
          totalIn: 0,
          totalOut: 0,
          avgScooters: 0,
          ridesPerScooter: 0,
          revenuePerScooter: 0
        };
        map.set(row.parking, item);
      }
      item.days += 1;
      item.totalRides += row.contractsCount;
      item.totalRevenue += row.revenue;
      item.totalCaptured += row.capturedRevenue;
      item.scooterDays += row.scooterCount;
      item.totalIn += row.scootersInCount;
      item.totalOut += row.scootersOutCount;
    });

    return Array.from(map.values()).map(item => ({
      ...item,
      avgScooters: item.days ? item.scooterDays / item.days : 0,
      ridesPerScooter: item.scooterDays ? item.totalRides / item.scooterDays : 0,
      revenuePerScooter: item.scooterDays ? item.totalRevenue / item.scooterDays : 0
    }));
  }, [data]);

  const totals = useMemo(() => {
    const totalRides = aggregated.reduce((acc, h) => acc + h.totalRides, 0);
    const totalRevenue = aggregated.reduce((acc, h) => acc + h.totalRevenue, 0);
    const scooterDays = aggregated.reduce((acc, h) => acc + h.scooterDays, 0);
    return {
      hotels: aggregated.length,
      totalRides,
      totalRevenue,
      avgRidesPerScooter: scooterDays ? totalRides / scooterDays : 0
    };
  }, [aggregated]);

  const getRecommendation = (hotel: AggregatedHotel): Recommendation => {
    if (!hotel.scooterDays || totals.avgRidesPerScooter === 0) return 'balanced';
    const ratio = hotel.ridesPerScooter / totals.avgRidesPerScooter;
    if (ratio >= ADD_THRESHOLD) return 'add';
    if (ratio <= RELOCATE_THRESHOLD) return 'relocate';
    return 'balanced';
  };

  const visibleHotels = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = term
      ? aggregated.filter(h => h.parkingName?.toLowerCase().includes(term))
      : aggregated;

    const direction = sortDir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      if (sortKey === 'parkingName') {
        return a.parkingName.localeCompare(b.parkingName) * direction;
      }
      return (a[sortKey] - b[sortKey]) * direction;
    });
  }, [aggregated, search, sortKey, sortDir]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'parkingName' ? 'asc' : 'desc');
    }
  };

  const formatCurrency = (value: number) =>
    `${value.toLocaleString('uk-UA', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })} €`;

  const formatNumber = (value: number, digits = 2) =>
    value.toLocaleString('uk-UA', {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits
    });

  const recommendationChip = (rec: Recommendation) => {
    if (rec === 'add') {
      return (
        <Tooltip title={t('rentalStats.recAddHint')}>
          <Chip
            icon={<TrendingUp />}
            label={t('rentalStats.recAdd')}
            size="small"
            color="success"
          />
        </Tooltip>
      );
    }
    if (rec === 'relocate') {
      return (
        <Tooltip title={t('rentalStats.recRelocateHint')}>
          <Chip
            icon={<TrendingDown />}
            label={t('rentalStats.recRelocate')}
            size="small"
            color="warning"
          />
        </Tooltip>
      );
    }
    return (
      <Chip
        icon={<Remove />}
        label={t('rentalStats.recBalanced')}
        size="small"
        variant="outlined"
      />
    );
  };

  const renderSortHeader = (key: SortKey, label: string, align: 'left' | 'right' = 'right') => (
    <TableHeaderCell align={align}>
      <TableSortLabel
        active={sortKey === key}
        direction={sortKey === key ? sortDir : 'asc'}
        onClick={() => handleSort(key)}
      >
        {label}
      </TableSortLabel>
    </TableHeaderCell>
  );

  return (
    <Root>
      <Title>{t('rentalStats.title')}</Title>
      <Typography variant="body2" color="textSecondary" sx={{ mt: 1, mb: 3 }}>
        {t('rentalStats.subtitle')}
      </Typography>

      <FilterSection>
        <TextField
          label={t('rentalStats.dateFrom')}
          type="date"
          size="small"
          value={dateFrom}
          onChange={e => setDateFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label={t('rentalStats.dateTo')}
          type="date"
          size="small"
          value={dateTo}
          onChange={e => setDateTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>{t('rentalStats.company')}</InputLabel>
          <Select
            label={t('rentalStats.company')}
            value={company}
            onChange={e => setCompany(e.target.value as string)}
          >
            <MenuItem value="">{t('rentalStats.allCompanies')}</MenuItem>
            {managementCompaniesState.companies.map(c => (
              <MenuItem key={c._id} value={c._id}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="contained" onClick={handleApply}>
          {t('rentalStats.apply')}
        </Button>
        <TextField
          label={t('rentalStats.searchPlaceholder')}
          size="small"
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ minWidth: 220 }}
        />
        <Typography variant="caption" color="textSecondary">
          {t('rentalStats.maxPeriodHint')}
        </Typography>
      </FilterSection>

      {error && (
        <Typography color="error" variant="body2" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard>
            <Typography variant="body2" color="textSecondary">
              {t('rentalStats.cardHotels')}
            </Typography>
            <Typography variant="h4">{totals.hotels}</Typography>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard>
            <Typography variant="body2" color="textSecondary">
              {t('rentalStats.cardTotalRides')}
            </Typography>
            <Typography variant="h4">{totals.totalRides}</Typography>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard>
            <Typography variant="body2" color="textSecondary">
              {t('rentalStats.cardTotalRevenue')}
            </Typography>
            <Typography variant="h4">{formatCurrency(totals.totalRevenue)}</Typography>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard>
            <Typography variant="body2" color="textSecondary">
              {t('rentalStats.cardAvgRidesPerScooter')}
            </Typography>
            <Typography variant="h4">{formatNumber(totals.avgRidesPerScooter)}</Typography>
          </StatCard>
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
        <Typography variant="body2" color="textSecondary">
          {t('rentalStats.legendTitle')}
        </Typography>
        {recommendationChip('add')}
        {recommendationChip('relocate')}
        {recommendationChip('balanced')}
      </Box>

      <TableContainer component={Paper} sx={{ borderRadius: '12px', overflow: 'auto' }}>
        {isFetching ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <Typography variant="body1" color="error">
              {t('rentalStats.loadError')}
            </Typography>
          </Box>
        ) : visibleHotels.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <Typography variant="body1" color="textSecondary">
              {t('rentalStats.emptyState')}
            </Typography>
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                {renderSortHeader('parkingName', t('rentalStats.colHotel'), 'left')}
                <TableHeaderCell>{t('rentalStats.colCompany')}</TableHeaderCell>
                <TableHeaderCell align="right">{t('rentalStats.colDays')}</TableHeaderCell>
                {renderSortHeader('avgScooters', t('rentalStats.colAvgScooters'))}
                {renderSortHeader('totalRides', t('rentalStats.colTotalRides'))}
                {renderSortHeader('ridesPerScooter', t('rentalStats.colRidesPerScooter'))}
                {renderSortHeader('totalRevenue', t('rentalStats.colRevenue'))}
                {renderSortHeader('revenuePerScooter', t('rentalStats.colRevenuePerScooter'))}
                <TableHeaderCell align="right">{t('rentalStats.colFlow')}</TableHeaderCell>
                <TableHeaderCell>{t('rentalStats.colRecommendation')}</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleHotels.map(hotel => {
                const rec = getRecommendation(hotel);
                const kpiColor =
                  rec === 'add'
                    ? 'success.main'
                    : rec === 'relocate'
                    ? 'warning.main'
                    : 'text.primary';
                return (
                  <StyledTableRow key={hotel.parking}>
                    <StyledTableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {hotel.parkingName}
                      </Typography>
                      {hotel.parkingAddress && (
                        <Typography variant="caption" color="textSecondary">
                          {hotel.parkingAddress}
                        </Typography>
                      )}
                    </StyledTableCell>
                    <StyledTableCell>
                      {hotel.managementCompanyName || t('rentalStats.noCompany')}
                    </StyledTableCell>
                    <StyledTableCell align="right">{hotel.days}</StyledTableCell>
                    <StyledTableCell align="right">
                      {formatNumber(hotel.avgScooters, 1)}
                    </StyledTableCell>
                    <StyledTableCell align="right">{hotel.totalRides}</StyledTableCell>
                    <StyledTableCell align="right">
                      <Typography variant="body2" fontWeight={700} color={kpiColor}>
                        {formatNumber(hotel.ridesPerScooter)}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      {formatCurrency(hotel.totalRevenue)}
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      {formatCurrency(hotel.revenuePerScooter)}
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      <Typography variant="body2" component="span" color="success.main">
                        +{hotel.totalIn}
                      </Typography>{' '}
                      /{' '}
                      <Typography variant="body2" component="span" color="error.main">
                        -{hotel.totalOut}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>{recommendationChip(rec)}</StyledTableCell>
                  </StyledTableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </Root>
  );
};

export default RentalStatisticsPage;
